import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { storeKeys } from '@/services/queryKeys';
import {
  storeService,
  type StoreSettings,
  type TenantStore,
  type ThemeTokens,
} from '@/services/store.service';
import { mockStores } from '@/mocks/stores';
import { isArrayResponse, hasKey, withMockFallback } from '@/utils/queryMock';
import { USE_MOCK } from '@/utils/env';

async function mockStoreSettings(): Promise<StoreSettings> {
  await new Promise((r) => setTimeout(r, 120));
  return {
    name: 'ERP Kim Hoàn - Chi nhánh 1',
    code: 'EG-CN01',
    address: '123 Đường Vàng',
    district: 'Quận 1',
    city: 'TP.HCM',
    phone: '0901234567',
    email: 'store1@erpgold.vn',
    taxCode: '0312345678',
    receiptFooter: 'Cảm ơn quý khách đã mua hàng!',
    workingHours: '08:00 - 21:00',
    goldTypes: ['SJC', '9999', '24K', '18K'],
    timezone: 'Asia/Ho_Chi_Minh',
    currency: 'VND',
  };
}

async function mockListStores(): Promise<TenantStore[]> {
  await new Promise((r) => setTimeout(r, 100));
  return mockStores;
}

export function useStoreSettings() {
  return useQuery({
    queryKey: storeKeys.settings(),
    queryFn: () =>
      withMockFallback(() => storeService.getSettings(), mockStoreSettings, {
        label: 'store.settings',
        validate: hasKey('name'),
      }),
    retry: 0,
  });
}

export function useUpdateStoreSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<StoreSettings>) =>
      withMockFallback(
        () => storeService.updateSettings(body),
        async () => ({ ...(await mockStoreSettings()), ...body }) as StoreSettings,
        { label: 'store.updateSettings' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.settings() }),
  });
}

export function useUpdateTheme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<ThemeTokens>) =>
      withMockFallback(
        () => storeService.updateTheme(body),
        async () => body as ThemeTokens,
        { label: 'store.updateTheme' },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.theme() }),
  });
}

async function mockUploadLogo(file: File): Promise<{ url: string }> {
  await new Promise((r) => setTimeout(r, 300));
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  return { url: dataUrl };
}

/** List stores (chi nhánh) để populate Select gán nhân viên (EG-85 §2). */
export function useStores() {
  return useQuery({
    queryKey: storeKeys.stores(),
    queryFn: () =>
      withMockFallback(() => storeService.listStores(), mockListStores, {
        label: 'store.listStores',
        validate: isArrayResponse,
      }),
    staleTime: 60_000,
    retry: 0,
  });
}

/** Single store detail (EG-354). */
export function useStore(id: string) {
  return useQuery({
    queryKey: storeKeys.store(id),
    queryFn: () => storeService.getStore(id),
    enabled: !!id,
    retry: 0,
  });
}

/** Create store mutation (EG-354). */
export function useCreateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      code?: string;
      address: string;
      phone?: string;
      managerId?: string;
      openingHours?: string;
      active?: boolean;
    }) => storeService.createStore(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.stores() }),
  });
}

/** Update store mutation (EG-354). */
export function useUpdateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      code?: string;
      address?: string;
      phone?: string;
      managerId?: string;
      openingHours?: string;
      active?: boolean;
    }) => storeService.updateStore(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      qc.invalidateQueries({ queryKey: storeKeys.store(vars.id) });
    },
  });
}

/** Deactivate store mutation (EG-354). */
export function useDeactivateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storeService.deactivateStore(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      qc.invalidateQueries({ queryKey: storeKeys.store(id) });
    },
  });
}

/** Delete store mutation (EG-1796). */
export function useDeleteStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storeService.deleteStore(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: storeKeys.stores() });
      qc.invalidateQueries({ queryKey: storeKeys.store(id) });
      qc.invalidateQueries({ queryKey: ['plan'] }); // invalidate plan info để update usage count ngay lập tức
    },
  });
}

export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      // Logo upload đặc biệt: mock mode → base64 data URL; real BE → multipart upload.
      if (USE_MOCK) return mockUploadLogo(file);
      try {
        return await storeService.uploadLogo(file);
      } catch {
        return mockUploadLogo(file);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: storeKeys.settings() }),
  });
}
